import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Upload, Loader2, AlertTriangle, Save } from 'lucide-react';
import { importUrls } from '../lib/QueryDataService';

const ImportUrls = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [urls, setUrls] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pre-populate fields from query string
  useEffect(() => {
    const dataParam = searchParams.get('data');
    if (dataParam) {
      try {
        const data = JSON.parse(dataParam);
        if (data.searchTerm) {
          setSearchTerm(data.searchTerm);
        }
        if (data.urls && Array.isArray(data.urls)) {
          setUrls(data.urls.join('\n'));
        }
      } catch (err) {
        console.error('Error parsing data parameter:', err);
      }
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!searchTerm.trim() || !urls.trim()) {
      return;
    }

    // Parse URLs - split by newlines and filter empty lines
    const urlList = urls
      .split('\n')
      .map(url => url.trim())
      .filter(url => url.length > 0)
      .map(url => ({ url }));

    if (urlList.length === 0) {
      setError(t('noUrlsProvided'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await importUrls({
        searchTerm: searchTerm.trim(),
        urls: urlList
      });
      
      // Redirect to query results page
      if (response && response.queryId) {
        navigate(`/query/${response.queryId}?searchTerm=${encodeURIComponent(response.searchTerm || searchTerm.trim())}`);
      }
    } catch (err) {
      console.error('Import error:', err);
      setError(err.message || t('importError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2 sm:gap-3">
            <Upload className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            {t('importUrls')}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('importUrlsSubtitle')}
          </p>
        </div>
      </div>

      {/* Import Form */}
      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-lg">{t('importUrlsFormTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Search Term / Title Field */}
            <div className="space-y-2">
              <label htmlFor="searchTerm" className="text-sm font-medium">
                {t('searchTermTitle')}
              </label>
              <Input
                id="searchTerm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('searchTermTitlePlaceholder')}
                required
              />
            </div>

            {/* URLs Field (Multiline) */}
            <div className="space-y-2">
              <label htmlFor="urls" className="text-sm font-medium">
                {t('urlsLabel')}
              </label>
              <Textarea
                id="urls"
                value={urls}
                onChange={(e) => setUrls(e.target.value)}
                placeholder={t('urlsPlaceholder')}
                className="min-h-[200px] font-mono text-sm"
                required
              />
              <p className="text-xs text-muted-foreground">
                {t('urlsHint')}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700">
                <AlertTriangle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || !searchTerm.trim() || !urls.trim()}
              className="flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('importing')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {t('save')}
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportUrls;

